import type { AlertColor } from "@mui/material/Alert/Alert";
import { useQueryClient as useTanStackQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import head from "lodash/head";
import { useParams } from "react-router-dom";

import useApi from "../../api/apiConfig";
import { saveFile } from "../../api/files";
import useQueryClient from "../../api/query";
import { fileToSource, toSourceFileId } from "../../api/sources";
import { getTodayISODate } from "../../utils/utils";
import { Action } from "../actions";
import { useCreateClaim } from "../ClaimsDashboard/hooks";
import { MANUAL_UPLOAD } from "../ClaimsTable/ClaimForm";
import {
  deleteClaimAndNotify,
  deleteProcedureAndNotify,
  saveProcedureAndNotify,
} from "../crud";
import { useClaimsPostingPoll } from "../polling";
import { postPMSUpdateAndNotify } from "../posting";
import { validateClaimAndNotify } from "../validation";
import {
  appendDetailClaim,
  applyPaymentsAction,
  type PaymentsCacheAction,
  paymentDetailKey,
  usePaymentDetail,
  useUpdateClaim,
  useUpdateDenial,
  useUpdateStringifyClaim,
} from "./hooks";
import PaymentsDetailsView from "./PaymentsDetailsView";

const NO_CLAIMS: ClaimWithProcedureAndPatientMessage[] = [];

interface PaymentDetailProps {
  selectedPractices: PracticeMessage[];
  allPractices: PracticeMessage[];
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
}

export default function PaymentDetail({
  selectedPractices,
  allPractices,
  updateSnackBar,
  supportedPayers,
  supportedSources,
}: PaymentDetailProps) {
  const { paymentId } = useParams();
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  const rqClient = useTanStackQueryClient();
  const detailQuery = usePaymentDetail(paymentId);
  const updateClaimMutation = useUpdateClaim();
  const updateDenialMutation = useUpdateDenial();
  const updateStringifyClaimMutation = useUpdateStringifyClaim();
  const createClaimMutation = useCreateClaim();

  const payment = detailQuery.data?.payment ?? null;
  const claims = detailQuery.data?.claims ?? NO_CLAIMS;

  const updateStore = (action: PaymentsCacheAction) =>
    applyPaymentsAction(rqClient, action);

  useClaimsPostingPoll(claims, queryClient, updateStore, updateSnackBar);

  const onSaveProcedure = async (procedure: ProcedureMessage) => {
    await saveProcedureAndNotify(
      procedure,
      claims,
      queryClient,
      updateStore,
      updateSnackBar,
    );
  };

  const updateClaimDeltas = async (
    existingClaim: ClaimWithProcedureAndPatientMessage,
    claim: ClaimMessage,
  ) => {
    const deltas = (
      Object.entries(claim) as [keyof ClaimMessage, unknown][]
    ).reduce<Partial<ClaimMessage>>(
      (acc, [key, value]) => {
        const existingValue =
          existingClaim[key] && key === "payer"
            ? (existingClaim[key] as string).toUpperCase()
            : existingClaim[key];
        if (value !== undefined && existingValue !== value) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Partial<ClaimMessage>,
    );
    if (Object.keys(deltas).length === 0) {
      updateSnackBar({
        severity: "warning",
        message: `Nothing to update`,
      });
      return;
    }
    try {
      const wieldyId = await updateStringifyClaimMutation.mutateAsync({
        ...deltas,
        wieldyId: claim.wieldyId,
        practiceId: claim.practiceId,
      });
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: {
          ...existingClaim,
          ...claim,
        },
      });
      updateSnackBar({
        severity: "success",
        message: `Successfully edited claim: ${wieldyId}`,
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const createClaim = async (claim: ClaimMessage, file: File | null) => {
    try {
      const fileResponse = file
        ? await saveFile(
            await getClient(),
            claim.practiceId,
            claim.source,
            file,
          )
        : null;
      const wieldyId = await createClaimMutation.mutateAsync({
        ...claim,
        fileId:
          claim.fileId !== MANUAL_UPLOAD
            ? claim.fileId
            : (fileResponse?.id ?? null),
      });

      const practice = allPractices.find(
        (p) => p.wieldyId === claim.practiceId,
      );
      const createdClaim: ClaimWithProcedureAndPatientMessage = {
        ...(claim as ClaimMessage),
        wieldyId,
        availableSince: getTodayISODate(),
        practice: practice?.name || "",
        practiceDisplayName: practice?.displayName || "",
        Patient: [],
        documents: (() => {
          if (
            payment &&
            claim.fileId === head(toSourceFileId(payment.documents))
          ) {
            return payment.documents;
          }
          return fileResponse ? fileToSource(fileResponse) : null;
        })(),
        Procedure: [],
      };
      if (paymentId) {
        appendDetailClaim(rqClient, paymentId, createdClaim);
      }
      updateSnackBar({
        severity: "success",
        message: `Successfully created claim: ${wieldyId}`,
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const onSaveClaim = async (claim: ClaimMessage, file: File | null) => {
    try {
      if (claim.wieldyId) {
        const existingClaim = claims.find((c) => c.wieldyId === claim.wieldyId);
        if (!existingClaim) {
          return;
        }
        await updateClaimDeltas(existingClaim, claim);
        return;
      }
      await createClaim(claim, file);
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const postToPMS = async (claim: ClaimWithProcedureAndPatientMessage) => {
    await postPMSUpdateAndNotify(
      claim,
      updateStore,
      queryClient,
      updateSnackBar,
    );
  };

  const updateClaim = async (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => {
    try {
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: { ...claim, ...claimWork },
      });
      await updateClaimMutation.mutateAsync(claimWork);
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const onDone = async (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => {
    try {
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: { ...claim, ...claimWork },
      });
      await updateDenialMutation.mutateAsync({ claimWork });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const onDenialUpdate = async (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => {
    try {
      const isSelection =
        denialWork.confirmAction !== true &&
        denialWork.userAction !== undefined;
      updateStore({
        type: "UPDATE_CLAIM",
        claimToUpdate: {
          ...claim,
          ...(denialWork.userAction !== undefined && {
            userAction: denialWork.userAction,
          }),
          ...(denialWork.urgency !== undefined && {
            denialUrgency: denialWork.urgency,
          }),
          ...(isSelection && { actionSelectedAt: new Date().toISOString() }),
        },
      });
      await updateDenialMutation.mutateAsync({
        claimWork: { wieldyId: claim.wieldyId, practiceId: claim.practiceId },
        denialWork,
      });
      const isConfirmedTransition =
        denialWork.confirmAction === true &&
        denialWork.userAction != null &&
        denialWork.userAction !== Action.CALL_PAYER;
      if (isConfirmedTransition && payment) {
        await rqClient.invalidateQueries({
          queryKey: paymentDetailKey(payment.wieldyId),
        });
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const validateClaim = async (claim: ClaimWithProcedureAndPatientMessage) => {
    await validateClaimAndNotify(
      claim,
      updateStore,
      queryClient,
      updateSnackBar,
    );
  };

  return (
    <PaymentsDetailsView
      claims={claims}
      updateClaim={updateClaim}
      postToPMS={postToPMS}
      payment={payment}
      loadingClaims={detailQuery.isPending}
      practices={selectedPractices}
      allPractices={allPractices}
      onSaveClaim={onSaveClaim}
      onDeleteClaim={async (claimKey) => {
        await deleteClaimAndNotify(
          claimKey,
          queryClient,
          updateStore,
          updateSnackBar,
        );
      }}
      onSaveProcedure={onSaveProcedure}
      onDeleteProcedure={async (procedure) => {
        await deleteProcedureAndNotify(
          procedure,
          claims,
          queryClient,
          updateStore,
          updateSnackBar,
        );
      }}
      supportedPayers={supportedPayers}
      supportedSources={supportedSources}
      validateClaim={validateClaim}
      onDone={onDone}
      onDenialUpdate={onDenialUpdate}
    />
  );
}
